import { useMemo, useState } from 'react'

import { useAuth } from '../auth'
import { local, queueOp } from '../lib/sync'
import { PRIORITY_LABEL, type Client, type Priority, type Source } from '../types'
import { IconChevron } from './icons'
import { Bar, Sheet } from './TaskCard'
import { useLocal } from './useLocal'

function todayISO(): string {
  const d = new Date()
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

export default function NewTask({ onBack, onToast }: {
  onBack: () => void
  onToast: (text: string) => void
}) {
  const { user } = useAuth()
  const { rows: clients } = useLocal<Client>(local.clients)
  const { rows: sources } = useLocal<Source>(local.sources)

  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [clientId, setClientId] = useState<number | null>(null)
  const [sourceId, setSourceId] = useState<number | null>(null)
  const [estimate, setEstimate] = useState('')
  const [priority, setPriority] = useState<Priority>('normal')
  const [workDate, setWorkDate] = useState(todayISO())
  const [startNow, setStartNow] = useState(false)
  const [sheet, setSheet] = useState<'client' | 'source' | 'priority' | null>(null)
  const [saving, setSaving] = useState(false)

  const activeClients = useMemo(
    () => clients.filter((c) => c.is_active).sort((a, b) => a.name.localeCompare(b.name, 'ru')),
    [clients],
  )

  const client = activeClients.find((c) => c.id === clientId) ?? null
  const source = sources.find((s) => s.id === sourceId) ?? null

  const estimateNum = estimate.trim() === '' ? null : Number(estimate.replace(',', '.'))
  const estimateBad = estimateNum !== null && (!Number.isFinite(estimateNum) || estimateNum < 0)

  // Канал обязателен, если у клиента не задан свой. Без этой проверки сервер
  // отклонит задачу, а в поле техник об этом узнает только вернувшись на связь.
  const needsSource = client !== null && client.source_id === null && sourceId === null

  const ready =
    title.trim().length > 0 && clientId !== null && !estimateBad && !needsSource && !saving

  const save = async () => {
    if (!ready) return
    setSaving(true)
    const { localId } = await queueOp('task.create', {
      title: title.trim(),
      description: description.trim() || null,
      client_id: clientId,
      source_id: sourceId,
      estimate: estimateNum,
      priority,
      work_date: workDate,
      assignee_id: user?.id ?? 0,
      // Имена нужны только черновику: он показывается до ответа сервера.
      client_name: client?.name ?? null,
      source_name: source?.name ?? null,
      assignee_name: user?.full_name ?? null,
    })
    if (startNow && localId !== null) {
      // Номера на сервере ещё нет. Ставим временный: очередь подменит его на
      // настоящий, когда создание уйдёт — операции идут строго по порядку.
      await queueOp('timer.start', { taskId: localId })
    }
    onToast(startNow ? 'Задача создана, таймер запущен' : 'Задача создана')
    onBack()
  }

  return (
    <>
      <Bar title="Новая задача" onBack={onBack} />

      <div className="m-block" style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div className="m-field">
          <label className="m-label" htmlFor="nt-title">Что делаем</label>
          <input
            id="nt-title"
            className="m-input"
            placeholder="Например: замена картриджа"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            autoFocus
          />
        </div>

        <div className="m-field">
          <span className="m-label">Клиент</span>
          <button className="m-input m-picker" onClick={() => setSheet('client')}>
            <span className={client ? '' : 'm-ph'}>{client?.name ?? 'Выберите клиента'}</span>
            <IconChevron color="#94a3b8" size={18} />
          </button>
        </div>

        <div className="m-field">
          <span className="m-label">Канал обращения</span>
          <button
            className="m-input m-picker"
            onClick={() => setSheet('source')}
            style={needsSource ? { borderColor: '#f0c98a' } : undefined}
          >
            <span className={source ? '' : 'm-ph'}>
              {source?.name ?? (client?.source?.name ? `${client.source.name} — по клиенту` : 'Выберите канал')}
            </span>
            <IconChevron color="#94a3b8" size={18} />
          </button>
          {needsSource && (
            <span style={{ fontSize: 13, color: '#a15c07' }}>
              У этого клиента канал по умолчанию не задан — выберите вручную
            </span>
          )}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div className="m-field">
            <label className="m-label" htmlFor="nt-date">Дата</label>
            <input
              id="nt-date"
              className="m-input"
              type="date"
              value={workDate}
              onChange={(e) => setWorkDate(e.target.value)}
            />
          </div>
          <div className="m-field">
            <label className="m-label" htmlFor="nt-est">Оценка, ч</label>
            <input
              id="nt-est"
              className="m-input"
              inputMode="decimal"
              placeholder="—"
              value={estimate}
              onChange={(e) => setEstimate(e.target.value)}
              style={estimateBad ? { borderColor: '#dc2626' } : undefined}
            />
          </div>
        </div>

        <div className="m-field">
          <span className="m-label">Приоритет</span>
          <button className="m-input m-picker" onClick={() => setSheet('priority')}>
            <span>{PRIORITY_LABEL[priority]}</span>
            <IconChevron color="#94a3b8" size={18} />
          </button>
        </div>

        <div className="m-field">
          <label className="m-label" htmlFor="nt-desc">Описание</label>
          <textarea
            id="nt-desc"
            className="m-input"
            placeholder="Подробности, если нужны"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>

        <label className="m-card" style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={startNow}
            onChange={(e) => setStartNow(e.target.checked)}
            style={{ width: 22, height: 22, accentColor: '#4db798' }}
          />
          <span style={{ fontSize: 15 }}>Сразу запустить таймер</span>
        </label>
      </div>

      <div style={{ height: 16 }} />

      <div className="m-actions">
        <button className="m-btn m-btn-ghost" onClick={onBack}>Отмена</button>
        <button className="m-btn m-btn-primary" disabled={!ready} onClick={() => void save()}>
          Создать
        </button>
      </div>

      {sheet === 'client' && (
        <Sheet title="Клиент" onClose={() => setSheet(null)}>
          {activeClients.length === 0 && <div className="m-empty">Клиентов нет в загруженных данных</div>}
          {activeClients.map((c) => (
            <button key={c.id} className="m-sheet-item" onClick={() => { setClientId(c.id); setSheet(null) }}>
              <span>{c.name}</span>
              {c.id === clientId && <span style={{ color: 'var(--m-green)', fontWeight: 700 }}>✓</span>}
            </button>
          ))}
        </Sheet>
      )}

      {sheet === 'source' && (
        <Sheet title="Канал обращения" onClose={() => setSheet(null)}>
          {client?.source && (
            <button className="m-sheet-item" onClick={() => { setSourceId(null); setSheet(null) }}>
              <span className="m-ph">{client.source.name} — по клиенту</span>
              {sourceId === null && <span style={{ color: 'var(--m-green)', fontWeight: 700 }}>✓</span>}
            </button>
          )}
          {sources.map((s) => (
            <button key={s.id} className="m-sheet-item" onClick={() => { setSourceId(s.id); setSheet(null) }}>
              <span>{s.name}</span>
              {s.id === sourceId && <span style={{ color: 'var(--m-green)', fontWeight: 700 }}>✓</span>}
            </button>
          ))}
        </Sheet>
      )}

      {sheet === 'priority' && (
        <Sheet title="Приоритет" onClose={() => setSheet(null)}>
          {(Object.keys(PRIORITY_LABEL) as Priority[]).map((p) => (
            <button key={p} className="m-sheet-item" onClick={() => { setPriority(p); setSheet(null) }}>
              <span>{PRIORITY_LABEL[p]}</span>
              {p === priority && <span style={{ color: 'var(--m-green)', fontWeight: 700 }}>✓</span>}
            </button>
          ))}
        </Sheet>
      )}
    </>
  )
}
